import type { ChangeEvent } from 'react';
import type {
  SuspiciousFieldsItem,
  SuspiciousFieldsProps,
} from '@/components/suspicious-fields/suspicious-fields.types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Control from '@/components/control';
import Dialog from '@/components/dialog';
import Input from '@/components/input';
import { useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import '@/components/suspicious-fields/suspicious-fields-v1.css';
import '@/components/suspicious-fields/suspicious-fields-v2.css';

const CONFIRM_LABEL = 'Das ist meine Nummer';
const CANCEL_LABEL = 'Zurück zum Formular';

export default function SuspiciousFields(props: SuspiciousFieldsProps) {
  const { t } = useTranslation();
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);

  const getField = (item: SuspiciousFieldsItem) => {
    if (!item.editable) {
      return (
        <>
          <span className='suspicious-fields__label'>{item.label}</span>
          <span className='suspicious-fields__value'>{item.value}</span>
        </>
      );
    }
    return cssVersion === 1 ? (
      <Input
        isSuccessful={item.state === 'success'}
        isUnsuccessful={item.state === 'error'}
        type='text'
        filter={item.filter}
        label={item.label}
        value={item.value}
        name={item.key}
        onChange={(e: ChangeEvent<HTMLInputElement>) => props.onChange(item.key, e.target.value)}
      />
    ) : (
      <Control.Text
        state={item.state}
        filter={item.filter}
        label={item.label}
        id={item.key}
        value={item.value}
        onChange={(value, id) => props.onChange(id, value)}
      />
    );
  };

  return (
    <Dialog
      withoutCloseButton
      doNotCloseOnOutsideClick
      on={props.on}
      position='top-center'
      size='1/3'
      onClose={props.onCancel}
    >
      <div data-css-version={cssVersion} className='suspicious-fields'>
        <h3 className='suspicious-fields__title'>{t('Ist das Ihre Nummer?')}</h3>
        <p className='suspicious-fields__intro'>
          {t(
            'Ihre Telefonnummer wirkt ungewöhnlich formatiert. Wir haben sie korrigiert — Sie können sie hier bei Bedarf noch anpassen.',
          )}
        </p>
        <div className='suspicious-fields__list'>
          {props.items.map((item) => (
            <div
              key={item.key}
              className={clsx('suspicious-fields__item', {
                'suspicious-fields__item--invalid': item.invalid,
              })}
            >
              {getField(item)}
              {item.reason && <span className='suspicious-fields__reason'>{item.reason}</span>}
            </div>
          ))}
        </div>
        <div className='suspicious-fields__actions'>
          {cssVersion === 1 ? (
            <>
              <button
                type='button'
                className='suspicious-fields__button suspicious-fields__button--confirm'
                onClick={props.onConfirm}
              >
                {t(CONFIRM_LABEL)}
              </button>
              <button
                type='button'
                className='suspicious-fields__button suspicious-fields__button--cancel'
                onClick={props.onCancel}
              >
                {t(CANCEL_LABEL)}
              </button>
            </>
          ) : (
            <>
              <Control.Button type='button' size='md' color='next' fullWidth onClick={props.onConfirm}>
                {t(CONFIRM_LABEL)}
              </Control.Button>
              <Control.Button type='button' size='md' color='previous' fullWidth onClick={props.onCancel}>
                {t(CANCEL_LABEL)}
              </Control.Button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}
